import {
	EventType,
	MinMaxSource,
	IChatMessagesOut,
	IChatEventWebMessageMessage,
	MessageType,
	logError,
	ChatProvider,
} from '@enuan/chat__utils';
import {
	isCustomEvent,
	mount,
	App,
	fetchConfiguration,
} from '@enuan/chat__lib';

let baseUrl: string;

class WebComponentChat extends HTMLElement {
	private _sendData(cmOut: IChatMessagesOut): Promise<string> {
		return Promise.reject();
	}
	public sendData(data: any) {
		const _data = typeof data === 'string' ? data : data;

		const event: IChatEventWebMessageMessage = {
			_type: MessageType.EVENT,
			_data: {
				name: EventType.WEB_MESSAGE,
				parameters: { payload: _data },
			},
		};
		this._sendData(event).catch((error) => {
			logError(
				'webcomponent:sendData',
				{ error, payload: { event } },
				{ baseUrl },
			);
		});
	}
	public minimize() {}
	public maximize() {}

	private emitPostMessage(type: string, payload: unknown) {
		if (window.parent !== window) {
			window.parent.postMessage({ type: `chat/${type}`, payload }, '*');
		}
	}

	connectedCallback() {
		const attributesName = this.getAttributeNames();
		const {
			params: _params,
			is_open,
			css,
			is_standalone,
			hide_popup,
			is_fullscreen,
			is_detached,
			// --------
			isopen, //! DEPRECATED
			fullscreen, //! DEPRECATED
		} = attributesName.reduce<Record<string, any>>(
			(prev, name) => ({ ...prev, [name]: this.getAttribute(name) }),
			{},
		);

		const getBoolean = (value: any) =>
			typeof value === 'undefined' ? undefined : value === 'true';

		const isOpen = getBoolean(is_open) ?? getBoolean(isopen);
		let isStandalone = getBoolean(is_standalone) ?? getBoolean(fullscreen);
		let hidePopup = getBoolean(hide_popup);
		let isFullscreen = getBoolean(is_fullscreen);
		let isDetached = getBoolean(is_detached);

		// Resolve the full BCP 47 locale tag (e.g. "it" → "it-IT") by maximizing
		// the likely subtags from the browser's reported language.
		const { language: _lang, region: _region } = new Intl.Locale(
			navigator.language,
		).maximize();
		const browserLanguage = _region ? `${_lang}-${_region}` : _lang;

		let params = { language: browserLanguage };
		try {
			params = { ...params, ...JSON.parse(_params) };
		} catch (error: any) {
			logError(
				'webcomponent:params',
				{ error, payload: { params: _params } },
				{ baseUrl },
			);
		}

		const shadowRoot = this.attachShadow({ mode: 'open' });

		const mountPoint = document.createElement('div');
		mountPoint.id = 'enuan_chat';
		shadowRoot.appendChild(mountPoint);

		const mainCssHref = new URL('./static/css/main.css', baseUrl).toString();
		const customCssHref = new URL(
			'./config/custom_style.css',
			baseUrl,
		).toString();

		const styleElement = (
			<>
				<link rel="stylesheet" href={mainCssHref} />
				<link rel="stylesheet" href={customCssHref} />
				{css && <style>{css.toString()}</style>}
			</>
		);

		this.addEventListener('sendData', (event) => {
			if (isCustomEvent(event)) {
				const { detail: data } = event;
				this.sendData(data);
			}
		});

		window.addEventListener('message', (event) => {
			if (event.source === window) return;
			if (!('type' in event.data) || !('payload' in event.data)) return;
			const message = event.data as { type: string; payload: any };
			if (!message.type.startsWith('chat/')) return;
			console.log('webcomponent processing message', message);
			if (message.type === 'chat/webMessage')
				return this.sendData(message.payload);
			if (message.type === 'chat/setMinimized') return this.minimize();
			if (message.type === 'chat/setMaximized') return this.maximize();
		});

		if (mountPoint) {
			const init = mount(mountPoint, {
				styleElement,
			});
			fetchConfiguration(baseUrl)
				.then(async (configuration) => {
					if (!configuration) {
						throw new Error('Configuration is missing');
					}
					const withCredentials =
						process.env.REACT_APP_FETCH_CREDENTIALS === 'true';

					if (process.env.NODE_ENV === 'production') {
						const apiInstance = new ChatProvider(
							baseUrl,
							configuration,
							withCredentials,
						);
						return { apiInstance, configuration };
					} else {
						const { FakeProvider } = await import('@enuan/chat__faker');
						const apiInstance = new FakeProvider();
						isStandalone =
							window.location.search.includes('standalone') || false;
						isFullscreen =
							window.location.search.includes('fullscreen') || false;
						hidePopup = window.location.search.includes('hide_popup') || false;
						isDetached = window.location.search.includes('detached') || false;
						return { apiInstance, configuration };
					}
				})
				.then(({ apiInstance, configuration }) => {
					init.render(
						<App
							instance={apiInstance}
							configuration={configuration}
							baseUrl={baseUrl}
							apiProps={{
								isStandalone,
								isFullscreen,
								hidePopup,
								isDetached,
								params,
								isOpen: isOpen
									? { isOpen, source: MinMaxSource.SITE }
									: undefined,
								onConnection: () => {
									this.dispatchEvent(new Event('init'));
									this.emitPostMessage('connected', true);
								},
								onMinimize: () => {
									this.dispatchEvent(
										new CustomEvent('chat-minimized', {
											bubbles: true,
											// TODO detail: ??,
										}),
									);
									this.emitPostMessage('minimized', true);
								},
								onMaximize: () => {
									this.dispatchEvent(
										new CustomEvent('chat-maximized', {
											bubbles: true,
											// TODO detail: ??,
										}),
									);
									this.emitPostMessage('minimized', false);
								},
								onWebMessage: (message: any) => {
									this.emitPostMessage(
										'webMessage',
										message._data.parameters.payload,
									);
									this.dispatchEvent(
										new CustomEvent('chat-webMessage', {
											bubbles: true,
											detail: message._data.parameters.payload,
										}),
									);
									// @deprecated
									this.emitPostMessage(
										'data',
										message._data.parameters.payload,
									);
									this.dispatchEvent(
										new CustomEvent('chat-data', {
											bubbles: true,
											detail: message._data.parameters.payload,
										}),
									);
								},
							}}
							ref={(e) => {
								if (e) {
									this.minimize = () => {
										e.toggleChat(false, MinMaxSource.SITE);
									};
									this.maximize = () => {
										e.toggleChat(true, MinMaxSource.SITE);
									};
									this._sendData = e.sendMessage ?? (() => {});
								}
							}}
						/>,
					);

					this.emitPostMessage('ready', true);
				})
				.catch((error) => {
					console.error('Chat failed to load', error);
					this.emitPostMessage('ready', false);
				});
		}
	}
}

export const renderWebcomponent = (url: string) => {
	baseUrl = url?.endsWith('/') ? url : url + '/';

	if (!customElements.get('enuan-chat')) {
		customElements.define('enuan-chat', WebComponentChat);
	}
};
